import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuthenticationState } from "../../../store/useAuthenticationState";
import { useUser } from "../../../store/useUser";
import { useUserSecurityState } from "../../../store/useUserSecurityState";
import { gql, useMutation } from "@apollo/client";
import { DiscountModal } from "./AutoLogoutComponents/discountModal";
import { Loader } from "lucide-react";
import { createPhpSession } from "./createSession";
import { toast } from "react-toastify";
import { AD_CLIENT_ID, AD_TENANT_ID, AD_REDIRECT_URI } from "../../../settings";
import { ComplianceStatus } from "./ComplianceStatus";
import { ComplianceItem } from "@/components/ui/TrainingListControl";
import { hasBlockingComplianceItems } from "./complianceGate";

const LOGIN_AZURE_AD = gql`
  mutation LoginAzureAD($code: String!) {
    loginAzureAD(code: $code) {
      token
      user {
        username
        realname
        policies
        id
        loginType
        settings {
          colorTheme
          dashboard
          adminSettingsFavorites
          systemSettingsFavorites
          centralPlannerPreferences {
            fullView
            focusColumns
            fullColumns
          }
          summaryFilters
          patientBannerExpanded
          ecmDashboardColumnPreferences {
            page
            visibleColumns
          }
          recentParticipants {
            participantId
            odrNumber
            name
            visitedAt
          }
        }
        compliance {
          id
          name
          item
          type
          description
          pdf
          frequencyDays
          lastCompletionDate
          nextDueDate
          dueInDays
          status
          required
          source
          actionPath
          actionLabel
        }
      }
    }
  }
`;

type FinalAuthStepResult = {
  token?: string;
  user?: any;
};

export const AzureADLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loginAzureAD] = useMutation<{ loginAzureAD: FinalAuthStepResult }>(LOGIN_AZURE_AD);
  const [isLoading, setIsLoading] = React.useState(false);
  const { setAuthenticationStateLogin } = useAuthenticationState();
  const { setUser, setIsUserLoading } = useUser();
  const { setSecurity, setIsSecurityLoading } = useUserSecurityState();
  const [showComplianceGate, setShowComplianceGate] = React.useState(false);
  const [redirectTarget, setRedirectTarget] = React.useState('/menu.php');
  const [loginCompliance, setLoginCompliance] = React.useState<ComplianceItem[] | null>(null);
  const processed = React.useRef(false);

  const resolveDashboardDestination = (sessionOk: boolean, dashboardSetting?: string) => {
    if (!sessionOk) {
      return '/dashboard';
    }
    const normalizedSetting = `${dashboardSetting || ''}`.trim().toUpperCase();
    if (normalizedSetting === 'DD2') {
      return '/dashboard';
    }
    return '/menu.php';
  };

  const handleAzureLogin = async (code:string) => {
    setIsLoading(true);
    try {
      const {data} = await loginAzureAD({ variables: { code } })
      const authResult = data?.loginAzureAD;
      const token = authResult?.token;
      if (!token) {
        throw new Error('Missing login token');
      }

      if (authResult.user) {
        setUser(authResult.user);
        if (setSecurity) {
          setSecurity(authResult.user);
        }
        if (Array.isArray(authResult.user.compliance)) {
          setLoginCompliance(authResult.user.compliance);
        }
      }
      if (setIsUserLoading) {
        setIsUserLoading(false);
      }
      if (setIsSecurityLoading) {
        setIsSecurityLoading(false);
      }

      setAuthenticationStateLogin(token);
      if (hasBlockingComplianceItems(authResult?.user?.compliance || [])) {
        setShowComplianceGate(true);
        return;
      }
      const sessionOk = await createPhpSession(token);
      const destination = resolveDashboardDestination(sessionOk, authResult?.user?.settings?.dashboard);
      setRedirectTarget(destination);
      setShowComplianceGate(true);
    } catch (err: any) {
      console.error('Azure Login Failed', err);
      toast.error(err.message || "Failed to login with Azure AD");
      setTimeout(() => {
        navigate("/login"); // Assumed login route is /login based on file name Login.tsx usually mapping there or similar
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    if (processed.current) return;

    const code = searchParams.get('code');    
    const error = searchParams.get('error');
    if (code) {
      processed.current = true;
      handleAzureLogin(code)
      return 
    } 
    
    if (error) {
      processed.current = true;
      toast.error(error);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      return 
    }

  }, [searchParams, loginAzureAD, navigate]);

  if (showComplianceGate) {
    return (
      <ComplianceStatus
        items={loginCompliance}
        onCleared={() => { window.location.href = redirectTarget; }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
          <Loader className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    )
  }

  return <></>

};

export default AzureADLogin; 
